import { useContext, useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 PRO components
import DashboardLayout from "parts/LayoutContainers/DashboardLayout";
import DashboardNavbar from "parts/Navbars/DashboardNavbar";
import Footer from "parts/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import ReportsLineChart from "parts/Charts/LineCharts/ReportsLineChart";
import DefaultItem from "parts/Items/DefaultItem";

import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";
import { useNotification } from "context/NotificationContext";

import { getAIReport, type AIReport } from "@shared/api/aiReports";

function AIReport() {
  const { t } = useContext(LanguageContext) as any;
  const { session } = useSessionContext();
  const { showNotification } = useNotification();

  const [report, setReport] = useState<AIReport | null>(null);

  const dummyReport = {
    labels: [
      t("AIReport.week1"),
      t("AIReport.week2"),
      t("AIReport.week3"),
      t("AIReport.week4"),
      t("AIReport.week5"),
      t("AIReport.week6"),
      t("AIReport.week7"),
    ],
    datasets: {
      label: t("AIReport.chat-title3"),
      data: [0, 0, 0, 0, 0, 0, 0],
    },
  };

  const defaultAdvice = [
    {
      icon: "info",
      title: t("AIReport.advice-title"),
      description: t("AIReport.advice-description"),
    },
  ];

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const data = await getAIReport(session.access_token);
        if (!data || (Array.isArray(data) && data.length === 0)) {
          setReport(null);
        } else {
          setReport(data);
        }
      } catch (err) {
        console.error("AIレポート取得エラー:", err);
        showNotification(
          {
            icon: "error",
            title: t("Common.error"),
            message: t("Common.get-error"),
          },
          3000
        );
      }
    };
    fetchReport();
  }, [session?.access_token]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mt={10}>
          <Card sx={{ height: "100%" }}>
            <MDBox padding="1rem">
              <MDBox
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
                py={2}
                pr={0.5}
                mt={-5}
                display="flex"
                justifyContent="center"
                alignItems="center"
                textAlign="center"
              >
                <MDTypography
                  variant="h5"
                  color="white"
                  mb={0}
                  sx={{ position: "relative", top: "0" }}
                >
                  {t("AIReport.comment")}
                </MDTypography>
              </MDBox>
              <MDTypography variant="h6" color="length" sx={{ mt: 3 }}>
                {report?.ai_comment ?? t("AIReport.no-comment")}
              </MDTypography>

              <MDBox pt={1} pb={1} px={1}>
                <Divider />
                <MDBox display="flex" alignItems="center">
                  <MDTypography
                    variant="button"
                    color="text"
                    lineHeight={1}
                    sx={{ mt: 0.15, mr: 0.5 }}
                  >
                    <Icon>schedule</Icon>
                  </MDTypography>
                  <MDTypography variant="button" color="text" fontWeight="light">
                    {report?.created_at?.slice(0, 10) ?? "ー"}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>

        <MDBox mt={4}>
          <MDTypography variant="h5" mb={2} sx={{ position: "relative", top: "-15px" }}>
            {t("AIReport.title")}
          </MDTypography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <ReportsLineChart
                color="dark"
                title={t("AIReport.chat-title1")}
                description={t("AIReport.chat-title2")}
                date={report?.created_at?.slice(0, 10) ?? ""}
                chart={{
                  labels:
                    Array.isArray(report?.report_data?.labels) &&
                    report.report_data.labels.length > 0
                      ? report.report_data.labels
                      : dummyReport.labels,
                  datasets: {
                    label: t("AIReport.chat-title3"),
                    data:
                      Array.isArray(report?.report_data?.data) && report.report_data.data.length > 0
                        ? report.report_data.data
                        : dummyReport.datasets.data,
                  },
                }}
              />
            </Grid>

            {/* strengths */}
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 2 }}>
                <MDTypography variant="h6" mb={2}>
                  {t("AIReport.strength")}
                </MDTypography>
                <MDBox display="flex" flexDirection="column" gap={2}>
                  {Array.isArray(report?.ai_advice?.strengths) &&
                  report.ai_advice.strengths.length > 0
                    ? report.ai_advice.strengths.map((desc, i) => (
                        <DefaultItem
                          key={`strength-${i}`}
                          icon="thumb_up"
                          color="primary"
                          title=""
                          description={desc}
                        />
                      ))
                    : defaultAdvice.map((item, i) => (
                        <DefaultItem
                          key={`default-strength-${i}`}
                          icon={item.icon}
                          color="primary"
                          title=""
                          description={item.description}
                        />
                      ))}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>

        {/* next_steps + areas_for_improvement 並列表示 */}
        <Grid container spacing={3} mt={1}>
          {/* next_steps */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2 }}>
              <MDTypography variant="h6" mb={2}>
                {t("AIReport.next-step")}
              </MDTypography>
              <MDBox display="flex" flexDirection="column" gap={2}>
                {Array.isArray(report?.ai_advice?.next_steps) &&
                report.ai_advice.next_steps.length > 0
                  ? report.ai_advice.next_steps.map((desc, i) => (
                      <DefaultItem
                        key={`next-${i}`}
                        icon="trending_up"
                        color="info"
                        title=""
                        description={desc}
                      />
                    ))
                  : defaultAdvice.map((item, i) => (
                      <DefaultItem
                        key={`default-next-${i}`}
                        icon={item.icon}
                        color="info"
                        title=""
                        description={item.description}
                      />
                    ))}
              </MDBox>
            </Card>
          </Grid>

          {/* areas_for_improvement */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2 }}>
              <MDTypography variant="h6" mb={2}>
                {t("AIReport.improvement")}
              </MDTypography>
              <MDBox display="flex" flexDirection="column" gap={2}>
                {Array.isArray(report?.ai_advice?.areas_for_improvement) &&
                report.ai_advice.areas_for_improvement.length > 0
                  ? report.ai_advice.areas_for_improvement.map((desc, i) => (
                      <DefaultItem
                        key={`improve-${i}`}
                        icon="build"
                        color="success"
                        title=""
                        description={desc}
                      />
                    ))
                  : defaultAdvice.map((item, i) => (
                      <DefaultItem
                        key={`default-improve-${i}`}
                        icon={item.icon}
                        color="success"
                        title=""
                        description={item.description}
                      />
                    ))}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AIReport;
